// File: src/components/lex/hooks/useProactiveContext.ts
// Proactive context-awareness hook for Lex widget (pinned mode).
// Detects module transitions and entity focus changes, then injects
// greeting messages and auto-summary requests.

import { useEffect, useRef } from 'react';
import type { LexContext, LexMessage } from '../../../services/universalLexClient';

interface UseProactiveContextParams {
  context: LexContext;
  isPinned: boolean;
  isOpen: boolean;
  userName: string;
  onInjectMessage: (msg: LexMessage) => void;
  onSendBackendMessage: (msg: string) => void;
}

const MODULE_LABELS: Record<string, string> = {
  matters: 'Matters',
  contacts: 'Contacts/Clients',
  documents: 'Documents',
  billing: 'Billing',
  calendar: 'Calendar',
  admin: 'Administration',
};

const ENTITY_DEBOUNCE_MS = 800;

function getPrevEntityLabel(prev: LexContext): string | null {
  if (prev.matterLabel) return prev.matterLabel;
  if (prev.clientName) return prev.clientName;
  return null;
}

function buildTransitionGreeting(
  userName: string,
  moduleLabel: string,
  prev: LexContext
): string {
  const prevLabel = getPrevEntityLabel(prev);
  const prevModule = MODULE_LABELS[prev.application] || prev.application;

  if (prevLabel) {
    return `${userName}, we've moved from the **${prevLabel}** ${prevModule.toLowerCase()} context to **${moduleLabel}**. Looking for something connected to that?`;
  }

  return `${userName}, I see we're now in **${moduleLabel}**. What are we working on here?`;
}

function getEntityKey(ctx: LexContext): { type: string; id: string } | null {
  if (ctx.matterId) return { type: 'matter', id: ctx.matterId };
  if (ctx.clientId) return { type: 'client', id: ctx.clientId };
  if (ctx.documentId) return { type: 'document', id: ctx.documentId };
  return null;
}

function buildSummaryPrompt(entityType: string, ctx: LexContext): string {
  switch (entityType) {
    case 'matter': {
      const name = ctx.matterLabel ? ` for **${ctx.matterLabel}**` : '';
      return `Provide a concise status summary${name} \u2014 key details, open tasks, upcoming deadlines, and billing status.`;
    }
    case 'client': {
      const name = ctx.clientName ? ` for **${ctx.clientName}**` : '';
      return `Provide a brief summary${name} \u2014 their role, associated matters, and recent activity.`;
    }
    case 'document':
      return 'Provide a brief summary of this document \u2014 type, key contents, and relevance.';
    default:
      return 'Provide a brief summary of the current context.';
  }
}

export function useProactiveContext({
  context,
  isPinned,
  isOpen,
  userName,
  onInjectMessage,
  onSendBackendMessage,
}: UseProactiveContextParams): void {
  const prevContextRef = useRef<LexContext | null>(null);
  const entityDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSummarizedEntityRef = useRef<{ type: string; id: string } | null>(null);

  // Stable refs for callbacks to avoid stale closures
  const onInjectRef = useRef(onInjectMessage);
  onInjectRef.current = onInjectMessage;
  const onSendRef = useRef(onSendBackendMessage);
  onSendRef.current = onSendBackendMessage;

  useEffect(() => {
    const isVisible = isPinned || isOpen;
    const prev = prevContextRef.current;

    // First mount: store context and exit. No greeting on initial render.
    if (!prev) {
      prevContextRef.current = { ...context };
      return;
    }

    // Widget not visible: track context silently, no messages.
    if (!isVisible) {
      prevContextRef.current = { ...context };
      return;
    }

    // --- 1. Module transition greeting ---
    if (context.application !== prev.application) {
      const moduleLabel = MODULE_LABELS[context.application] || context.application;
      const greeting: LexMessage = {
        id: `ctx-transition-${Date.now()}`,
        sender: 'lex',
        message: buildTransitionGreeting(userName, moduleLabel, prev),
        timestamp: new Date(),
        metadata: {
          intent: 'context_transition',
          confidence: 1,
        },
      };
      onInjectRef.current(greeting);

      // Reset last-summarized entity on module switch so the first
      // entity in the new module always gets an auto-summary.
      lastSummarizedEntityRef.current = null;
    }

    // --- 2. Entity focus auto-summary ---
    const currentEntity = getEntityKey(context);
    const prevEntity = getEntityKey(prev);

    const entityChanged =
      currentEntity !== null &&
      (prevEntity === null ||
        currentEntity.type !== prevEntity.type ||
        currentEntity.id !== prevEntity.id);

    if (entityChanged && currentEntity) {
      // Clear any pending debounce
      if (entityDebounceRef.current) {
        clearTimeout(entityDebounceRef.current);
      }

      const entitySnapshot = { ...currentEntity };
      const contextSnapshot = { ...context };
      entityDebounceRef.current = setTimeout(() => {
        entityDebounceRef.current = null;

        // Skip if this exact entity was already summarized
        const last = lastSummarizedEntityRef.current;
        if (last && last.type === entitySnapshot.type && last.id === entitySnapshot.id) {
          return;
        }

        lastSummarizedEntityRef.current = entitySnapshot;
        onSendRef.current(buildSummaryPrompt(entitySnapshot.type, contextSnapshot));
      }, ENTITY_DEBOUNCE_MS);
    }

    // --- 3. Update previous context ---
    prevContextRef.current = { ...context };

    return () => {
      if (entityDebounceRef.current) {
        clearTimeout(entityDebounceRef.current);
      }
    };
  }, [context, isPinned, isOpen, userName]);
}
